import { KeyboardEvent, useMemo, useRef, useState } from "react";
import { QuizQuestionType } from "../../types/Quiz";
import {
  getTextForLanguage,
  QuizFormQuestion,
} from "./systemQuizModel";
import { parseInterpretationFormula } from "./interpretationFormula";
import s from "./InterpretationFormulaEditor.module.css";

type InterpretationFormulaEditorProps = {
  value: string;
  onChange: (value: string) => void;
  questions: QuizFormQuestion[];
  selectedLanguage: string;
  inputLabel?: string;
  inputId?: string;
};

const questionTypeLabels: Record<QuizQuestionType, string> = {
  [QuizQuestionType.SINGLE]: "Single choice",
  [QuizQuestionType.MULTIPLE]: "Multiple choice",
  [QuizQuestionType.RANGE]: "Numeric range",
  [QuizQuestionType.TEXT]: "Text",
};

const getQuestionTitle = (
  question: QuizFormQuestion,
  selectedLanguage: string,
) =>
  getTextForLanguage(question.text, selectedLanguage) ||
  question.text.find((item) => item.text.trim())?.text ||
  "Untitled question";

const InterpretationFormulaEditor = ({
  value,
  onChange,
  questions,
  selectedLanguage,
  inputLabel = "Interpretation Formula",
  inputId = "interpretation-formula",
}: InterpretationFormulaEditorProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [suggestionQuery, setSuggestionQuery] = useState<string | null>(null);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const referenceablePositions = useMemo(
    () =>
      questions
        .map((question, index) =>
          question.type === QuizQuestionType.TEXT ? null : index + 1,
        )
        .filter((position): position is number => position !== null),
    [questions],
  );
  const parseResult = useMemo(
    () =>
      parseInterpretationFormula(value, questions.length, {
        referenceablePositions,
      }),
    [questions.length, referenceablePositions, value],
  );
  const referencedPositions = useMemo(
    () =>
      Array.from(
        new Set(
          Array.from(value.matchAll(/[Qq](\d+)/g)).map((match) =>
            Number(match[1]),
          ),
        ),
      ),
    [value],
  );
  const suggestions = questions
    .map((question, index) => ({ question, position: index + 1 }))
    .filter(({ question }) => question.type !== QuizQuestionType.TEXT)
    .filter(({ position }) =>
      `Q${position}`.startsWith((suggestionQuery ?? "Q").toUpperCase()),
    );

  const updateSuggestions = (nextValue: string, cursor: number) => {
    const match = nextValue.slice(0, cursor).match(/[Qq]\d*$/);
    setSuggestionQuery(match?.[0] ?? null);
    setActiveSuggestion(0);
  };

  const insertReference = (position: number) => {
    const input = inputRef.current;
    const cursor = input?.selectionStart ?? value.length;
    const queryLength = suggestionQuery?.length ?? 0;
    const insertion = `Q${position}`;
    const nextValue = `${value.slice(0, cursor - queryLength)}${insertion}${value.slice(cursor)}`;
    const nextCursor = cursor - queryLength + insertion.length;

    onChange(nextValue);
    setSuggestionQuery(null);
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCursor, nextCursor);
    }, 0);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (suggestionQuery === null || !suggestions.length) return;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveSuggestion(
        (current) =>
          (current + direction + suggestions.length) % suggestions.length,
      );
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      insertReference((suggestions[activeSuggestion] ?? suggestions[0]).position);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setSuggestionQuery(null);
    }
  };

  return (
    <div className={s.editor}>
      <label className={s.label} htmlFor={inputId}>
        Formula
      </label>
      <div className={s.inputWrap}>
        <input
          id={inputId}
          ref={inputRef}
          className={`${s.input} ${!parseResult.valid ? s.inputInvalid : ""}`}
          aria-label={inputLabel}
          aria-invalid={!parseResult.valid}
          aria-describedby={`${inputId}-error`}
          autoComplete="off"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            updateSuggestions(
              event.target.value,
              event.target.selectionStart ?? event.target.value.length,
            );
          }}
          onClick={(event) =>
            updateSuggestions(value, event.currentTarget.selectionStart ?? value.length)
          }
          onKeyDown={handleKeyDown}
        />
        {suggestionQuery !== null && suggestions.length > 0 && (
          <div className={s.suggestions} role="listbox" aria-label="Question references">
            {suggestions.map(({ question, position }, index) => (
              <button
                key={question.id}
                type="button"
                role="option"
                aria-selected={index === activeSuggestion}
                className={`${s.suggestion} ${
                  index === activeSuggestion ? s.suggestionActive : ""
                }`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => insertReference(position)}
              >
                <strong>Q{position}</strong>
                <span>{getQuestionTitle(question, selectedLanguage)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {!parseResult.valid && (
        <p className={s.error} id={`${inputId}-error`} role="alert">
          {parseResult.error}
        </p>
      )}

      {referencedPositions.length > 0 && (
        <div className={s.references} aria-label="Formula references">
          {referencedPositions.map((position) => {
            const question = questions[position - 1];
            const title = question
              ? getQuestionTitle(question, selectedLanguage)
              : "Unknown question";
            const isReferenceable =
              Boolean(question) && question?.type !== QuizQuestionType.TEXT;
            const details = question
              ? `${title} · ${questionTypeLabels[question.type]} · Position ${position}`
              : `Q${position} does not exist`;

            return (
              <button
                type="button"
                key={position}
                className={`${s.referenceChip} ${
                  !isReferenceable ? s.referenceInvalid : ""
                }`}
                title={details}
              >
                Q{position} <span>{title}</span>
              </button>
            );
          })}
        </div>
      )}

      <details className={s.help}>
        <summary>Formula syntax help</summary>
        <p>
          Use Q1, Q2, and other visible question positions with integer
          constants, parentheses, unary minus, and +, -, *, /. Lowercase
          q references are accepted and saved as uppercase Q.
          Text questions keep their global position but cannot be referenced.
        </p>
        <code>(Q1 * 3) + Q2</code>
        <code>-Q1 + (Q2 / 2)</code>
      </details>
    </div>
  );
};

export default InterpretationFormulaEditor;
